'use client'
import { themeBackgroundBlurMap } from '@mntn-dev/ui-theme'
import { cn } from '@mntn-dev/ui-utilities'
import {
  type Table as ReactTableType,
  type Row,
  flexRender,
} from '@tanstack/react-table'
import type { Virtualizer } from '@tanstack/react-virtual'
import { type ComponentType, type ReactNode, useRef } from 'react'
import {
  type UseWindowVirtualizerProps,
  useWindowVirtualizer,
} from '../../hooks/use-window-virtualizer.ts'
import { Surface } from '../surface/surface.tsx'
import { Table } from '../table/table'
import { Text } from '../text/text.tsx'
import type { DataTableRowProps } from './data-table-row.tsx'

type Props<T> = {
  className?: string
  emptyState?: ReactNode
  table: ReactTableType<T>
  fullHeight?: boolean
  fullWidth?: boolean
  tableId?: string
  rowIdentifier?: string
  scroll?: boolean
  loading?: boolean
  onClick?: (data: T) => void
  rowComponent: ComponentType<DataTableRowProps<T>>
  windowVirtualizerOptions?: Omit<UseWindowVirtualizerProps<Element>, 'count'>
}

function VirtualRows<T>({
  onClick,
  rowComponent: RowComponent,
  rowIdentifier = '',
  rows,
  tableId,
  virtualizer,
}: {
  onClick: (data: T) => () => void
  rows: Row<T>[]
  virtualizer: Virtualizer<Window, Element>
} & Pick<Props<T>, 'rowComponent' | 'tableId' | 'rowIdentifier' | 'onClick'>) {
  return virtualizer.getVirtualItems().map((virtualRow, index) => {
    const row = rows[virtualRow.index]

    if (!row) {
      return null
    }
    return (
      <RowComponent
        key={row.id}
        row={row}
        data={row.original}
        tableId={tableId}
        rowIdentifier={rowIdentifier}
        onClick={onClick(row.original)}
        style={{
          height: `${virtualRow.size}px`,
          transform: `translateY(${
            virtualRow.start -
            virtualizer.options.scrollMargin -
            index * virtualRow.size
          }px)`,
        }}
      />
    )
  })
}

function DefaultRows<T>({
  onClick,
  rowComponent: RowComponent,
  rowIdentifier = '',
  rows,
  tableId,
}: { rows: Row<T>[]; onClick: (data: T) => () => void } & Pick<
  Props<T>,
  'rowComponent' | 'rowIdentifier' | 'tableId' | 'onClick'
>) {
  return rows.map((row) => (
    <RowComponent
      key={row.id}
      row={row}
      data={row.original}
      tableId={tableId}
      rowIdentifier={rowIdentifier}
      onClick={onClick(row.original)}
    />
  ))
}

export const DataTable = <T,>({
  className = '',
  emptyState = 'No data',
  fullHeight = false,
  fullWidth = false,
  rowComponent,
  rowIdentifier = '',
  scroll = false,
  table,
  tableId,
  windowVirtualizerOptions: windowVirtualizerOptionsProp,
  onClick,
}: Props<T>) => {
  const parentRef = useRef<HTMLTableSectionElement>(null)

  const handleRowClick = (data: T) => () => {
    if (onClick) {
      onClick(data)
    }
  }

  const virtualizationOptions = windowVirtualizerOptionsProp ?? {
    estimateSize: () => 72,
    enabled: false,
  }
  const { rows } = table.getRowModel()

  const [virtualizer, VirtualizationProvider] = useWindowVirtualizer({
    count: rows.length,
    scrollMargin: parentRef.current?.offsetTop ?? 0,
    ...virtualizationOptions,
  })

  return (
    <VirtualizationProvider value={virtualizer}>
      <Surface
        className={className}
        height={fullHeight ? 'full' : 'fit'}
        width={fullWidth ? 'full' : 'fit'}
      >
        <div ref={parentRef} className={cn('h-full w-full rounded-lg')}>
          <div
            className="w-full relative"
            style={{
              height: virtualizationOptions.enabled
                ? `${virtualizer.getTotalSize() + 72}px`
                : undefined,
            }}
          >
            <Table
              width={fullWidth ? 'full' : 'fit'}
              id={tableId}
              dataTestId={`${tableId}-table`}
              className={cn({
                'absolute inset-0': scroll,
              })}
            >
              <Table.Head
                background="container-secondary"
                className={cn(themeBackgroundBlurMap['table-head-blur'], {
                  'sticky top-0': scroll,
                  'z-10': scroll,
                })}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row
                    key={headerGroup.id}
                    dataTestId={`${tableId}-table-header-row`}
                    className="h-18"
                  >
                    {headerGroup.headers.map((header) => (
                      <Table.HeaderCell
                        key={header.id}
                        className={cn(
                          header.column.columnDef.meta?.className || '',
                          {
                            'cursor-pointer select-none':
                              header.column.getCanSort(),
                          }
                        )}
                        canSort={header.column.getCanSort()}
                        direction={header.column.getIsSorted()}
                        onClick={header.column.getToggleSortingHandler()}
                        dataTestId={`${tableId}-table-header-cell-${header.id}`}
                        dataTrackingId={`${tableId}-table-header-cell-${header.id}`}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Table.HeaderCell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Head>
              <Table.Body background={null}>
                {rows.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colspan={table.getAllColumns().length}>
                      <div className="p-8">
                        {typeof emptyState === 'string' ? (
                          <Text fontSize="base" textColor="secondary">
                            {emptyState}
                          </Text>
                        ) : (
                          emptyState
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : virtualizationOptions.enabled ? (
                  <VirtualRows
                    virtualizer={virtualizer}
                    tableId={tableId}
                    rowComponent={rowComponent}
                    rowIdentifier={rowIdentifier}
                    rows={rows}
                    onClick={handleRowClick}
                  />
                ) : (
                  <DefaultRows
                    tableId={tableId}
                    rowComponent={rowComponent}
                    rowIdentifier={rowIdentifier}
                    rows={rows}
                    onClick={handleRowClick}
                  />
                )}
              </Table.Body>
            </Table>
          </div>
        </div>
      </Surface>
    </VirtualizationProvider>
  )
}
